import { useState } from 'react';
import AuthService from 'core/auth/auth.service';
import { BASE_PATH_PROD, BASE_URL_SVIL, HOSTNAME_PRODUCTION } from 'env';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';

interface OperationFilterSavedInputDto {
  userId: number;
  operation: string;
  jsonFiltri: string;
}

const useSaveOperationFilters = (operationName: string) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getBasePath = () => {
    return window.location.hostname !== HOSTNAME_PRODUCTION ? BASE_URL_SVIL : BASE_PATH_PROD;
  };

  const getHeaders = () => {
    const token = AuthService.getToken();
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  };

  const saveFilters = async (filters: any): Promise<void> => {
    const user = AuthService.getUser();
    if (!user || !user.userId) {
      AppToastService.error('Utente non autenticato');
      return;
    }

    setIsSaving(true);
    try {
      const payload: OperationFilterSavedInputDto = {
        userId: user.userId,
        operation: operationName,
        jsonFiltri: JSON.stringify(filters),
      };

      const response = await fetch(`${getBasePath()}/operationFilter/create`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      AppToastService.success('Filtri salvati con successo');
    } catch (error) {
      console.error('Error saving filters:', error);
      AppToastService.error('Errore durante il salvataggio dei filtri');
    } finally {
      setIsSaving(false);
    }
  };

  const loadFilters = async (): Promise<any | null> => {
    const user = AuthService.getUser();
    if (!user || !user.userId) {
      return null;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${getBasePath()}/operationFilter/search/${user.userId}/${operationName}`,
        {
          method: 'GET',
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Nessun filtro salvato, non è un errore
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const jsonString = await response.text();
      if (jsonString) {
        return JSON.parse(jsonString);
      }
      return null;
    } catch (error) {
      console.error('Error loading filters:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { saveFilters, loadFilters, isSaving, isLoading };
};

export default useSaveOperationFilters;
